import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { LogOut, Calendar, Clock, User, Mail, Phone, MessageSquare, Filter, Search, RefreshCw, Trash2 } from 'lucide-react';

const AdminDashboard = () => {
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Check if user is logged in
  useEffect(() => {
    const isAuth = sessionStorage.getItem('adminAuth');
    if (!isAuth) {
      navigate('/admin');
    }
  }, [navigate]);

  // Fetch bookings
  const fetchBookings = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/bookings`);
      const data = await response.json();
      
      if (response.ok) {
        setBookings(data);
        setFilteredBookings(data);
      } else {
        toast.error('Failed to fetch bookings');
      }
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast.error('Error loading bookings');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
    
    // Auto-refresh every 15 seconds
    const intervalId = setInterval(() => {
      fetchBookings();
    }, 15000);
    
    // Cleanup interval on unmount
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter bookings
  useEffect(() => {
    let filtered = bookings;

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(b => b.status === statusFilter);
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(b =>
        b.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        b.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        b.phone.includes(searchTerm) ||
        b.service.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredBookings(filtered);
  }, [statusFilter, searchTerm, bookings]);

  // Update booking status
  const updateStatus = async (bookingId, newStatus) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/bookings/${bookingId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success(`Booking ${newStatus}!`);
        fetchBookings(); // Refresh the list
      } else {
        toast.error('Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Error updating booking');
    }
  };

  // Delete booking
  const deleteBooking = async (bookingId, customerName) => {
    // Confirmation dialog
    const confirmDelete = window.confirm(
      `Are you sure you want to delete the appointment for ${customerName}?\n\nThis action cannot be undone.`
    );
    
    if (!confirmDelete) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/bookings/${bookingId}`, {
        method: 'DELETE'
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success('Booking deleted successfully');
        fetchBookings(); // Refresh the list
      } else {
        toast.error('Failed to delete booking');
      }
    } catch (error) {
      console.error('Error deleting booking:', error);
      toast.error('Error deleting booking');
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('adminToken');
    sessionStorage.removeItem('adminAuth');
    toast.success('Logged out successfully');
    navigate('/admin');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'confirmed':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'completed':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const stats = {
    total: bookings.length,
    pending: bookings.filter(b => b.status === 'pending').length,
    confirmed: bookings.filter(b => b.status === 'confirmed').length,
    completed: bookings.filter(b => b.status === 'completed').length,
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-teal-50 to-emerald-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-cyan-600 to-teal-600 text-white rounded-lg shadow-xl p-6 mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold mb-2">Admin Dashboard</h1>
              <p className="text-cyan-100">Shubh Wellness Clinic - Appointment Management</p>
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="bg-white text-cyan-600 hover:bg-cyan-50 border-2"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </Button>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="border-2 border-cyan-200">
            <CardContent className="p-4">
              <div className="text-sm text-gray-600">Total Bookings</div>
              <div className="text-3xl font-bold text-cyan-600">{stats.total}</div>
            </CardContent>
          </Card>
          <Card className="border-2 border-yellow-200">
            <CardContent className="p-4">
              <div className="text-sm text-gray-600">Pending</div>
              <div className="text-3xl font-bold text-yellow-600">{stats.pending}</div>
            </CardContent>
          </Card>
          <Card className="border-2 border-green-200">
            <CardContent className="p-4">
              <div className="text-sm text-gray-600">Confirmed</div>
              <div className="text-3xl font-bold text-green-600">{stats.confirmed}</div>
            </CardContent>
          </Card>
          <Card className="border-2 border-blue-200">
            <CardContent className="p-4">
              <div className="text-sm text-gray-600">Completed</div>
              <div className="text-3xl font-bold text-blue-600">{stats.completed}</div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6 border-2 border-cyan-200">
          <CardHeader>
            <CardTitle className="flex items-center text-cyan-700">
              <Filter className="mr-2 h-5 w-5" />
              Filters & Search
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Status Filter</label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="border-2 border-cyan-200">
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Search</label>
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search by name, email, phone..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 border-2 border-cyan-200"
                  />
                </div>
              </div>
              <div className="flex items-end">
                <Button
                  onClick={fetchBookings}
                  className="w-full bg-gradient-to-r from-cyan-600 to-teal-600 hover:from-cyan-700 hover:to-teal-700"
                >
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Refresh
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bookings List */}
        <Card className="border-2 border-cyan-200">
          <CardHeader>
            <CardTitle className="text-cyan-700">
              Appointments ({filteredBookings.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-8 text-gray-500">Loading bookings...</div>
            ) : filteredBookings.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No bookings found</div>
            ) : (
              <div className="space-y-4">
                {filteredBookings.map((booking) => (
                  <Card key={booking.id} className="border border-gray-200 hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        {/* Customer Info */}
                        <div className="md:col-span-2">
                          <div className="flex items-center mb-2">
                            <User className="h-4 w-4 text-cyan-600 mr-2" />
                            <span className="font-bold text-lg">{booking.name}</span>
                          </div>
                          <div className="space-y-1 text-sm text-gray-600">
                            <div className="flex items-center">
                              <Mail className="h-3 w-3 text-cyan-600 mr-2" />
                              <a href={`mailto:${booking.email}`} className="hover:text-cyan-600">
                                {booking.email}
                              </a>
                            </div>
                            <div className="flex items-center">
                              <Phone className="h-3 w-3 text-cyan-600 mr-2" />
                              <a href={`tel:${booking.phone}`} className="hover:text-cyan-600">
                                {booking.phone}
                              </a>
                            </div>
                            {booking.notes && (
                              <div className="flex items-start mt-2">
                                <MessageSquare className="h-3 w-3 text-cyan-600 mr-2 mt-0.5" />
                                <span className="text-xs italic">{booking.notes}</span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Appointment Details */}
                        <div>
                          <div className="space-y-2 text-sm">
                            <div className="flex items-center">
                              <Calendar className="h-4 w-4 text-cyan-600 mr-2" />
                              <span className="font-medium">{booking.date}</span>
                            </div>
                            <div className="flex items-center">
                              <Clock className="h-4 w-4 text-cyan-600 mr-2" />
                              <span>{booking.time}</span>
                            </div>
                            <div className="mt-2">
                              <span className="text-xs text-gray-500">Service:</span>
                              <div className="font-semibold text-cyan-700">{booking.service}</div>
                            </div>
                          </div>
                        </div>

                        {/* Status & Actions */}
                        <div className="flex flex-col justify-between">
                          <div className={`px-3 py-1 rounded-full text-xs font-semibold text-center border-2 ${getStatusColor(booking.status)}`}>
                            {booking.status.toUpperCase()}
                          </div>
                          <Select
                            value={booking.status}
                            onValueChange={(newStatus) => updateStatus(booking.id, newStatus)}
                          >
                            <SelectTrigger className="mt-2 border-2 border-cyan-200">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">Pending</SelectItem>
                              <SelectItem value="confirmed">Confirmed</SelectItem>
                              <SelectItem value="completed">Completed</SelectItem>
                              <SelectItem value="cancelled">Cancelled</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button
                            onClick={() => deleteBooking(booking.id, booking.name)}
                            variant="outline"
                            size="sm"
                            className="mt-2 border-2 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                          >
                            <Trash2 className="h-3 w-3 mr-1" />
                            Delete
                          </Button>
                          <div className="text-xs text-gray-400 mt-2">
                            ID: {booking.id.substring(0, 8)}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;
